import { ApiError, ChatDone, PollResult, SurveyAnswer } from './types';
import { dlog } from './util';

// The backend wraps every JSON reply in the global ResponseInterceptor
// envelope { success, statusCode, ..., data } — unwrap `data` here so the rest
// of the widget sees plain payloads. Errors carry { message, persianMessage,
// errorCode } (bilingual custom exceptions). SSE bypasses the envelope.

function toApiError(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as Record<string, unknown>;
  const raw = b.message;
  // validation-pipe errors ship message as an array of constraint strings
  const message = Array.isArray(raw) ? raw.join('; ') : typeof raw === 'string' ? raw : 'request failed';
  return {
    status,
    message,
    persianMessage: typeof b.persianMessage === 'string' ? b.persianMessage : undefined,
    errorCode: typeof b.errorCode === 'string' ? b.errorCode : undefined,
  };
}

export class Api {
  constructor(
    private readonly base: string,
    private readonly siteKey: string,
  ) {}

  private headers(): Record<string, string> {
    return { 'Content-Type': 'application/json', 'X-Site-Key': this.siteKey };
  }

  /** origin the socket server lives on (strips the `/api/v1` path off the base) */
  socketOrigin(): string {
    try {
      return new URL(this.base).origin;
    } catch {
      return this.base.replace(/\/api\/v\d+\/?$/, '');
    }
  }

  siteKeyValue(): string {
    return this.siteKey;
  }

  async post<T>(path: string, body: unknown, keepalive = false): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.base + path, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify(body),
        keepalive,
      });
    } catch (err) {
      throw toApiError(0, { message: String(err) });
    }
    return this.unwrap<T>(res);
  }

  async get<T>(path: string): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.base + path, { headers: { 'X-Site-Key': this.siteKey } });
    } catch (err) {
      throw toApiError(0, { message: String(err) });
    }
    return this.unwrap<T>(res);
  }

  private async unwrap<T>(res: Response): Promise<T> {
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      /* empty / non-JSON body */
    }
    if (!res.ok) throw toApiError(res.status, json);
    const obj = json as Record<string, unknown> | null;
    return (obj && typeof obj === 'object' && 'data' in obj ? obj.data : obj) as T;
  }

  /**
   * POST /public/chat and consume the SSE stream (docs/widget.md §4):
   * `data: {delta}` → onDelta · `{done,…}` resolves · `{error,…}` rejects.
   * Guard/validation failures arrive as plain JSON *before* the stream starts —
   * detected via content-type and rethrown through the same ApiError shape.
   */
  async chatStream(
    body: { message: string; anonId: string; conversationId?: string; pageUrl?: string },
    onDelta: (delta: string) => void,
  ): Promise<ChatDone> {
    let res: Response;
    try {
      res = await fetch(this.base + '/public/chat', {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify(body),
      });
    } catch (err) {
      throw toApiError(0, { message: String(err) });
    }

    const contentType = res.headers.get('content-type') ?? '';
    if (!res.ok || contentType.indexOf('text/event-stream') === -1) {
      let json: unknown = null;
      try {
        json = await res.json();
      } catch {
        /* ignore */
      }
      throw toApiError(res.status, json);
    }
    if (!res.body) throw toApiError(0, { message: 'streaming not supported' });

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done: ChatDone | null = null;

    const handleFrame = (frame: string) => {
      for (const line of frame.split('\n')) {
        const trimmed = line.replace(/\r$/, '');
        if (!trimmed.startsWith('data:')) continue;
        let payload: Record<string, unknown>;
        try {
          payload = JSON.parse(trimmed.slice(5).trim());
        } catch {
          dlog('unparseable SSE frame', trimmed);
          continue;
        }
        if (payload.error) throw toApiError((payload.statusCode as number) ?? 0, payload);
        if (payload.done) done = payload as unknown as ChatDone;
        else if (typeof payload.delta === 'string') onDelta(payload.delta);
      }
    };

    try {
      for (;;) {
        const { value, done: eof } = await reader.read();
        if (eof) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          handleFrame(frame);
        }
      }
    } catch (err) {
      if ((err as ApiError).status !== undefined && (err as ApiError).message !== undefined) throw err;
      throw toApiError(0, { message: String(err) }); // network drop mid-stream
    }

    if (!done) throw toApiError(0, { message: 'stream ended without done event' });
    return done;
  }

  /** pre-chat gate: store the name + phone the visitor volunteered (config.requireContactInfo).
   *  The server opens the conversation right away and returns its id so the widget adopts it. */
  identify(body: {
    anonId: string;
    name: string;
    phone: string;
  }): Promise<{ saved: boolean; conversationId?: string }> {
    return this.post('/public/identify', body);
  }

  /** widget receive channel: operator replies newer than `after` (ISO cursor) */
  pollMessages(body: {
    conversationId: string;
    anonId: string;
    after?: string;
  }): Promise<PollResult> {
    return this.post<PollResult>('/public/poll', body);
  }

  /** submit the visitor's answers to an operator-sent form */
  submitForm(body: {
    conversationId: string;
    anonId: string;
    promptMessageId: string;
    answers: Record<string, unknown>;
    pageUrl?: string;
  }): Promise<{ saved: boolean; messageId: string }> {
    return this.post('/public/forms/submit', body);
  }

  /** submit the visitor's answers to an operator-sent survey */
  submitSurvey(body: {
    conversationId: string;
    anonId: string;
    promptMessageId: string;
    answers: SurveyAnswer[];
    pageUrl?: string;
  }): Promise<{ saved: boolean; messageId: string }> {
    return this.post('/public/surveys/chat-submit', body);
  }

  /** unload-time event flush: keepalive fetch survives page teardown */
  flushOnUnload(body: unknown): void {
    try {
      void fetch(this.base + '/public/events', {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {
        /* page is going away; best-effort only */
      });
    } catch {
      // very old engines without keepalive: sendBeacon with ?siteKey= (guard
      // accepts the query form; text/plain avoids the beacon preflight limits)
      try {
        navigator.sendBeacon(
          `${this.base}/public/events?siteKey=${encodeURIComponent(this.siteKey)}`,
          JSON.stringify(body),
        );
      } catch {
        /* give up silently */
      }
    }
  }
}
